import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { Trash2, NotebookPen, Lock, Check } from "lucide-react";
import { toast } from "sonner";

export default function Bookmarks() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bookmarks");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/bookmarks/${id}`);
    toast.success(lang === "es" ? "Marcador eliminado" : "Bookmark removed");
    load();
  };

  const saveNote = async (id, note) => {
    try {
      const res = await api.patch(`/bookmarks/${id}/note`, { note });
      setItems((prev) => prev.map((b) => (b.bookmark_id === id ? { ...b, ...res.data } : b)));
      return true;
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(lang === "es" ? "Solo para sostenedores" : "Supporters only");
      } else {
        toast.error(lang === "es" ? "No se pudo guardar" : "Could not save");
      }
      return false;
    }
  };

  return (
    <div>
      <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">FaithCycle</p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {t("bookmarksTitle", lang)}
      </h1>

      <div className="mt-10 max-w-3xl space-y-4">
        {loading && <p className="text-[var(--text-secondary)]">{t("loading", lang)}</p>}
        {!loading && items.length === 0 && (
          <p className="text-[var(--text-secondary)]">{t("noBookmarks", lang)}</p>
        )}
        {items.map((b) => (
          <BookmarkRow
            key={b.bookmark_id}
            bookmark={b}
            lang={lang}
            isSupporter={!!user?.is_supporter}
            onOpen={() => navigate(`/read?b=${b.book}&c=${b.chapter}&v=${b.verse}`)}
            onDelete={() => remove(b.bookmark_id)}
            onSaveNote={(text) => saveNote(b.bookmark_id, text)}
          />
        ))}
      </div>
    </div>
  );
}

function BookmarkRow({ bookmark: b, lang, isSupporter, onOpen, onDelete, onSaveNote }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(b.note || "");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const initialRef = useRef(b.note || "");
  const timerRef = useRef(null);

  // Debounced auto-save while typing
  useEffect(() => {
    if (!expanded) return;
    if (note === initialRef.current) return;
    if (!isSupporter) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaving(true);
      const ok = await onSaveNote(note);
      setSaving(false);
      if (ok) {
        initialRef.current = note;
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1500);
      }
    }, 800);
    return () => clearTimeout(timerRef.current);
  }, [note, expanded, isSupporter, onSaveNote]);

  const hasNote = (b.note || "").trim().length > 0;

  return (
    <div
      data-testid={`bookmark-${b.bookmark_id}`}
      className="group border border-[var(--border)] rounded-2xl p-4 bg-[var(--surface-elevated)]"
    >
      <div className="flex items-start gap-4">
        <button onClick={onOpen} className="flex-1 text-left min-w-0">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] mb-1">
            {b.book_name} {b.chapter}:{b.verse} · {b.translation}
          </p>
          <p className="font-serif-body text-lg group-hover:text-[var(--accent)] transition-colors duration-200">
            {b.text}
          </p>
        </button>
        <button
          data-testid={`toggle-note-${b.bookmark_id}`}
          onClick={() => setExpanded((v) => !v)}
          className={`p-2 rounded-full transition-colors duration-200 ${
            hasNote ? "text-[var(--accent)]" : "text-[var(--text-secondary)] hover:text-[var(--accent)]"
          }`}
          aria-label={lang === "es" ? "Notas personales" : "Personal notes"}
        >
          <NotebookPen className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <button
          data-testid={`delete-bookmark-${b.bookmark_id}`}
          onClick={onDelete}
          className="text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors duration-200 p-2"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-[var(--border)]" data-testid={`notepad-${b.bookmark_id}`}>
          {isSupporter ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)]">
                  {lang === "es" ? "Mis pensamientos" : "My thoughts"}
                </p>
                <span className="text-[10px] text-[var(--text-secondary)] flex items-center gap-1">
                  {saving && (lang === "es" ? "Guardando…" : "Saving…")}
                  {!saving && savedFlash && (
                    <>
                      <Check className="w-3 h-3 text-[var(--accent)]" strokeWidth={2} />
                      {lang === "es" ? "Guardado" : "Saved"}
                    </>
                  )}
                </span>
              </div>
              <textarea
                data-testid={`note-textarea-${b.bookmark_id}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  lang === "es"
                    ? "Escribe una reflexión, oración o revelación sobre este versículo…"
                    : "Write a reflection, prayer or revelation about this verse…"
                }
                rows={5}
                maxLength={4000}
                className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-xl p-3 text-sm leading-relaxed resize-y focus:outline-none focus:border-[var(--accent)] transition-colors duration-200"
              />
              <p className="text-[10px] text-[var(--text-secondary)] mt-1 text-right">
                {note.length} / 4000
              </p>
            </>
          ) : (
            <div className="flex flex-col items-start gap-3 py-2">
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                <p className="text-sm">
                  {lang === "es"
                    ? "Notas personales disponibles solo para sostenedores."
                    : "Personal notes available for supporters only."}
                </p>
              </div>
              <button
                data-testid={`upgrade-cta-${b.bookmark_id}`}
                onClick={() => navigate("/support")}
                className="text-xs uppercase tracking-widest px-4 h-9 rounded-full bg-[var(--accent)] text-white hover:opacity-90 transition-opacity duration-200"
              >
                {lang === "es" ? "Hazte sostenedor" : "Become a supporter"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
